"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function MiniPage({ variant }: { variant: 'a' | 'b' }) {
    const isWinner = variant === 'b';
    return (
        <div className="flex flex-col gap-2 p-3">
            <div className="h-2.5 rounded-full" style={{ width: '70%', background: 'rgba(8,4,0,0.1)' }} />
            <div className="h-2 rounded-full" style={{ width: '90%', background: 'rgba(8,4,0,0.07)' }} />
            <div className="h-2 rounded-full" style={{ width: '55%', background: 'rgba(8,4,0,0.07)' }} />
            <div
                className="mt-2 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold"
                style={{
                    fontFamily: CLASH,
                    background: isWinner ? PURPLE : 'rgba(8,4,0,0.12)',
                    color: isWinner ? '#ffffff' : 'rgba(8,4,0,0.4)',
                    letterSpacing: '0.04em',
                }}
            >
                {isWinner ? 'BOOK A DEMO' : 'LEARN MORE'}
            </div>
        </div>
    );
}

function ABTestPanel() {
    return (
        <div className="relative w-full max-w-[420px] mx-auto lg:mx-0">
            <motion.span
                initial={{ opacity: 0, y: -8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em]"
                style={{ fontFamily: CLASH, background: 'rgba(55,48,163,0.08)', border: '1px solid rgba(55,48,163,0.18)', color: PURPLE }}
            >
                <motion.span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: PURPLE }}
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                />
                Live Experiment
            </motion.span>

            <div className="relative grid grid-cols-2 gap-4">
                {/* VS badge */}
                <div
                    className="absolute top-[74px] left-1/2 -translate-x-1/2 z-20 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ background: INK, color: LIME, fontFamily: CLASH, boxShadow: '0 6px 16px -4px rgba(8,4,0,0.4)' }}
                >
                    VS
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24, rotate: 0 }}
                    whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.35, ease: 'easeOut' }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ background: CARD, border: '1.5px solid rgba(8,4,0,0.1)', boxShadow: '0 20px 50px -20px rgba(8,4,0,0.2)' }}
                >
                    <div className="flex items-center gap-1.5 px-3 py-2.5" style={{ borderBottom: '1px solid rgba(8,4,0,0.08)' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                        <span className="ml-1 text-[9px] font-bold uppercase tracking-wide" style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.4)' }}>
                            Variant A
                        </span>
                    </div>
                    <MiniPage variant="a" />
                    <div className="px-3 pb-3 flex items-center justify-between">
                        <span className="text-[10px] font-mono" style={{ color: 'rgba(8,4,0,0.4)' }}>Control</span>
                        <span className="text-base font-bold" style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.5)' }}>2.4%</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24, rotate: 0 }}
                    whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ background: CARD, border: `1.5px solid ${LIME}`, boxShadow: '0 20px 50px -16px rgba(206,255,26,0.35)' }}
                >
                    <motion.span
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 1, ease: 'backOut' }}
                        className="absolute -top-2.5 -right-2.5 z-10 text-[9px] font-bold px-2 py-1 rounded-full"
                        style={{ fontFamily: CLASH, background: LIME, color: INK, boxShadow: '0 6px 14px -4px rgba(8,4,0,0.3)' }}
                    >
                        WINNER
                    </motion.span>
                    <div className="flex items-center gap-1.5 px-3 py-2.5" style={{ borderBottom: '1px solid rgba(8,4,0,0.08)' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                        <span className="ml-1 text-[9px] font-bold uppercase tracking-wide" style={{ fontFamily: CLASH, color: PURPLE }}>
                            Variant B
                        </span>
                    </div>
                    <MiniPage variant="b" />
                    <div className="px-3 pb-3 flex items-center justify-between">
                        <span className="text-[10px] font-mono" style={{ color: 'rgba(8,4,0,0.4)' }}>Challenger</span>
                        <span className="text-base font-bold" style={{ fontFamily: CLASH, color: PURPLE }}>4.1%</span>
                    </div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.1 }}
                className="mt-6 flex items-center justify-center gap-4 rounded-2xl px-5 py-4"
                style={{ background: INK }}
            >
                <span className="text-2xl font-bold" style={{ fontFamily: CLASH, color: LIME }}>+71%</span>
                <span className="w-px h-8" style={{ background: 'rgba(255,255,255,0.15)' }} />
                <div className="flex flex-col leading-tight">
                    <span className="text-xs font-semibold" style={{ fontFamily: MANROPE, color: '#ffffff' }}>
                        Conversion Lift
                    </span>
                    <span className="text-[11px] font-mono" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        12,400 sessions · 95% confidence
                    </span>
                </div>
            </motion.div>
        </div>
    );
}

export default function CROHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-36 pb-24 overflow-hidden"
                style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
            >
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
                />
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: '-18%', right: '-10%', width: 620, height: 560,
                        background: 'radial-gradient(circle, rgba(55,48,163,0.12) 0%, transparent 68%)',
                        borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                    }}
                />

                <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                    <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/performance-marketing" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                Performance Marketing
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>Conversion Rate Optimization</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-[3.2rem] font-bold leading-[1.1] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            CRO That Gets More From{' '}
                            <span style={{ color: PURPLE }}>the Traffic You Already Have</span>
                        </motion.h1>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-4 max-w-xl"
                        >
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                More traffic isn&apos;t always the answer. Sometimes the bigger opportunity is converting more of the people already visiting your website.
                            </p>
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Lead Workz combines analytics, user behaviour, UX, messaging and experimentation to identify where potential customers drop off — and what can be improved.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.32 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                                style={{ background: PURPLE, color: '#ffffff', fontFamily: CLASH, letterSpacing: '0.08em' }}
                            >
                                Get a CRO Audit →
                            </button>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <ABTestPanel />
                    </div>

                </div>
            </div>
        </section>
    );
}
